import React, { useEffect } from 'react';
import { BackButton, Input, ErrorAlert, Checkbox, Button, ErrorMessage } from '../Components/Simple';
import useAsyncPricerApi from '../Hooks/useAsyncPricerApi';
import { ApiError, EntryTable, EntryTableColumn, ProviderMapper } from '../Components/Combined';
import { HH as modelType } from '../Constants/modelTypes';
import useSessionState from '../Hooks/useSessionState';
import { useNavigate, useLocation } from 'react-router-dom';
import useCollection from '../Hooks/useCollection';
import { formatDateStringIfMatches, getDefaultDateString } from '../Utils/stringUtils';
import useCalendarYear from '../Hooks/useCalendarYear';
import useExportRequest from '../Hooks/useExportRequest';
import useImportRequest from '../Hooks/useImportRequest';
import { mapToSetters } from '../Utils/mapping';
import FilePickerButton from '../Components/Simple/FilePickerButton';
import { API_DATE_STRING_FORMAT, DATE_STRING_FORMAT } from '../Constants/stringFormats';
import { enableAsyncPricingApi } from '../Utils/features';
import { transformStringToUpCase } from '../Utils/valueTransforms';
import usePricerApi from '../Hooks/usePricerApi';

export default function HH() {
    const location = useLocation();
    const { prePopulatedMedicareId, prePopulatedNPI, prePopulatedBillType } = location.state || {};
    const [claimId, setClaimId] = useSessionState('', 'claimId');
    const [medicareId, setMedicareId] = useSessionState(prePopulatedMedicareId ?? '', 'medicareId');
    const [beneficiaryZipCode, setBeneficiaryZipCode] = useSessionState('', 'beneficiaryZipCode');
    const [admitDate, setAdmitDate] = useSessionState(getDefaultDateString(), 'adminDate');
    const [fromDate, setFromDate] = useSessionState(getDefaultDateString(), 'fromDate');
    const [toDate, setToDate] = useSessionState(getDefaultDateString(), 'toDate');
    const [dischargeStatus, setDischargeStatus] = useSessionState('', 'dischargeStatus');
    const [billType, setBillType] = useSessionState(prePopulatedBillType ?? '', 'billType');
    const [hasConditionCode47, setHasConditionCode47] = useSessionState(false, 'hasConditionCode47');
    const [rapDate, setRapDate] = useSessionState('', 'rapDate');
    const [billedAmountTotal, setBilledAmountTotal] = useSessionState('', 'billedAmountTotal');
    const [providerName, setProviderName] = useSessionState('', 'providerName');
    const [npi, setNpi] = useSessionState(prePopulatedNPI ?? '', 'npi');
    const [calendarYear, setCalendarYearInputDate] = useCalendarYear(null);

    const navigate = useNavigate();

    const getNewLine = (index) => {
        return {
            lineId: index + 1,
            revenueCode: '',
            procedureCode: '',
            serviceDate: '',
            visits: 0,
            units: 0
        };
    };

    const getDefaultLines = () => {
        return [getNewLine(0), getNewLine(1), getNewLine(2), getNewLine(3), getNewLine(4)];
    };

    const linesCollection = useCollection(getDefaultLines(), getNewLine, 'lines');

    const [fileExporting, _fileExportSuccess, fileExportError, fileExportExecute, fileExportReset] = useExportRequest(modelType, {
        claimId,
        medicareId,
        beneficiaryZipCode,
        admitDate,
        fromDate,
        toDate,
        dischargeStatus,
        billType,
        hasConditionCode47,
        rapDate,
        lines: linesCollection.values,
        providerName,
        npi
    });

    const [fileImporting, fileImportSuccess, fileImportError, fileImportResult, fileImportReset, fileImportOnChange] = useImportRequest(modelType);

    const pricer = enableAsyncPricingApi ? useAsyncPricerApi(modelType) : usePricerApi(modelType);
    const warmupPricer = useAsyncPricerApi(modelType, true);

    const handleSubmit = async () => {
        fileExportReset();
        fileImportReset();


        if (enableAsyncPricingApi) {
            await pricer.execute(formatRequest());
        } else {
            await pricer.execute(calendarYear, formatRequest());
        }
    };

    const handleFileSave = () => {
        pricer.reset();
        fileExportExecute();
    };

    const handleLoadOnChange = (e) => {
        pricer.reset();
        fileImportOnChange(e);
    };

    const formatRequest = () => {
        return {
            claimId: claimId || null,
            medicareId: medicareId || null,
            beneficiaryZip: beneficiaryZipCode || null,
            admitDate: formatDateStringIfMatches(admitDate || null, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            fromDate: formatDateStringIfMatches(fromDate || null, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            toDate: formatDateStringIfMatches(toDate || null, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            dischargeStatus: dischargeStatus || null,
            billType: billType || null,
            condCode47: hasConditionCode47 ? 'Y' : 'N',
            rapSubmissionDate: formatDateStringIfMatches((calendarYear >= 2020 && rapDate) || null, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            serviceLineItems: linesCollection.values.filter(l => l.revenueCode).map(l => {
                return {
                    lineId: !l.lineId && l.lineId !== 0 ? null : l.lineId.toString(),
                    revCode: l.revenueCode || null,
                    HCPCS: l.procedureCode || null,
                    serviceDate: formatDateStringIfMatches(l.serviceDate || null, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
                    visits: !l.visits && l.visits !== 0 ? null : l.visits,
                    unitsDays: !l.units && l.units !== 0 ? null : l.units
                }
            })
        };
    }
    const handleReset = () => {
        setClaimId('');
        setMedicareId('');
        setBeneficiaryZipCode('');
        setAdmitDate(getDefaultDateString());
        setFromDate(getDefaultDateString());
        setToDate(getDefaultDateString());
        setDischargeStatus('');
        setBillType('');
        setHasConditionCode47(false);
        setRapDate('');
        linesCollection.setValues(getDefaultLines());
        setProviderName('');
        setNpi('');
        pricer.reset();
        fileImportReset();
        fileExportReset();
    };

    useEffect(() => {
        setCalendarYearInputDate(toDate);
    }, [toDate]);


    useEffect(() => {
        if (fileImportSuccess) {
            const setters = {
                claimId: setClaimId,
                medicareId: setMedicareId,
                beneficiaryZipCode: setBeneficiaryZipCode,
                admitDate: setAdmitDate,
                fromDate: setFromDate,
                toDate: setToDate,
                dischargeStatus: setDischargeStatus,
                billType: setBillType,
                hasConditionCode47: setHasConditionCode47,
                rapDate: setRapDate,
                lines: linesCollection.setValues,
                providerName: setProviderName,
                npi: setNpi
            };
            handleReset();
            mapToSetters(fileImportResult, setters);
        }
    }, [fileImportSuccess]);

    useEffect(() => {
        if (pricer.success) {
            navigate('result', {
                state: {
                    calendarYear,
                    providerName,
                    result: pricer.result,
                    billedAmountTotal
                }
            });
        }
    }, [pricer.success]);

    return (
        <div className="py-2">
            <div className="row my-2 justify-content-between sticky top g-0 border-bottom align-items-center">
                <div className="col-auto">
                    <h3>Home Health Pricer</h3>
                    <div className="fs-6">
                        Enter all claim information requested below. Please press SUBMIT when complete to calculate the prospective payment amount.
                    </div>
                </div>
                <div className="col-auto">
                    <BackButton />
                </div>
            </div>
            {(pricer.error || fileImportError || fileExportError) && (<div>
                <ApiError error={pricer.error} />
                {fileImportError ? <ErrorMessage errors={[{ message: fileImportError }]} /> : null}
                {fileExportError ? <ErrorMessage errors={[{ message: fileExportError }]} /> : null}
            </div>)}
            <form onSubmit={handleSubmit}>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input id="claim-id" labelClassName="fw-bold" label="Claim ID" onChange={setClaimId} value={claimId} />
                    </div>
                    <ProviderMapper inputs={[
                        {
                            name: "providerName",
                            label: "Provider Name",
                            superscript: "1",
                            value: providerName,
                            onChange: setProviderName
                        },
                        {
                            name: "medicareId",
                            label: "Medicare ID",
                            superscript: "1",
                            value: medicareId,
                            onChange: setMedicareId
                        },
                        {
                            name: "npi",
                            label: "NPI",
                            sublabel: "(facilities may have multiple NPIs)",
                            superscript: "1",
                            value: npi,
                            onChange: setNpi
                        }
                    ]} model={modelType} />
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="Beneficiary's Zip Code" maxLength={5} labelClassName="fw-bold" id="beneficiarys-zip-code" onChange={setBeneficiaryZipCode} value={beneficiaryZipCode} />
                    </div>
                    <div className="col-md-3">
                        <Input label="Admit Date" labelClassName="fw-bold" id="admit-date" type="date" onChange={setAdmitDate} value={admitDate} placeholder="MM/DD/YYYY" />
                    </div>
                    <div className="col-md-3">
                        <Input label="From Date" labelClassName="fw-bold" id="from-date" type="date" onChange={setFromDate} value={fromDate} placeholder="MM/DD/YYYY" />
                    </div>
                    <div className="col-md-3">
                        <Input label="To Date" labelClassName="fw-bold" id="to-date" type="date" onChange={setToDate} value={toDate} placeholder="MM/DD/YYYY" />
                    </div>
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="Discharge Status" labelClassName="fw-bold" id="discharge-status" onChange={setDischargeStatus} value={dischargeStatus} />
                    </div>
                    <div className="col-md-3">
                        <Input label="Type of Bill" labelClassName="fw-bold" id="bill-type" onChange={setBillType} value={billType} />
                    </div>
                    <div className="col-md-3 mt-4">
                        <Checkbox label="Claim Has Condition Code 47" labelClassName="fw-bold" id="has-condition-code-47" onChange={setHasConditionCode47} value={hasConditionCode47} />
                    </div>
                    <div className="col-md-3">
                        {calendarYear && calendarYear < 2020 && <Input label="Request for Anticipated Payment (RAP) Date (optional)" labelClassName="fw-bold" id="rap-date" type="text" value="N/A" disabled={true} />}
                        {(!calendarYear || calendarYear >= 2020) && <Input label="Request for Anticipated Payment (RAP) Date (optional)" labelClassName="fw-bold" id="rap-date" type="date" onChange={setRapDate} value={rapDate} placeholder="MM/DD/YYYY" />}
                    </div>
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="Billed Amount - Total" labelClassName="fw-bold" id="billed-amount-total" onChange={setBilledAmountTotal} value={billedAmountTotal} placeholder="0000.00" />
                    </div>
                </div>
                <div> <sup> 1 </sup> Pricing depends only on Medicare ID </div>
                <hr />
                <div className="row g-0">
                    <div className="col my-2">
                        {calendarYear !== 2024 ? (
                            <div className="fst-italic">
                                At least two lines must be populated for pricing: One with revenue code = 0023, and at least one with revenue code 042x, 043x, 044x, 055x, 056x, or 057x.
                            </div>
                        ) : (
                            <div className="fst-italic">
                                At least two lines must be populated for pricing: One with revenue code = 0023, and at least one with revenue code 042x, 043x, 044x, 055x, 056x, or 057x. Disposable Negative Pressure Would Therapy (NPWT) devices must be entered using HCPCS A9272 with revenue code 027x, except 0274.
                            </div>
                        )}
                        <EntryTable id="service-line-items"
                            collection={linesCollection}
                            lineCountLabel="Total Lines"
                            upDownNavigation={true}
                            max={100}>
                            <EntryTableColumn label="Line ID"
                                name="lineId"
                                type="label"
                                className="mw-50" />
                            <EntryTableColumn label="Revenue Code"
                                name="revenueCode"
                                type="text" />
                            <EntryTableColumn label="HIPPS/HCPCS Code"
                                name="procedureCode"
                                type="text"
                                valueTransform={transformStringToUpCase} />
                            <EntryTableColumn label="Date of Service"
                                name="serviceDate"
                                type="date"
                                className="mw-125"
                                placeholder="MM/DD/YYYY" />
                            <EntryTableColumn label="Visits "
                                name="visits"
                                type="number"
                                className="mw-75" />
                            <EntryTableColumn label="Units"
                                name="units"
                                type="number"
                                className="mw-75" />
                        </EntryTable>
                    </div>
                </div>
                <div className="row justify-content-between my-2 sticky bottom g-0 border-top">
                    <div className="col-auto align-self-center">
                        <div className="row g-0">
                            <div className="col-auto ms-2">
                                <Button className="btn-outline-secondary" id="save-request-button"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    onClick={handleFileSave}
                                    text="Save" />
                            </div>
                            <div className="col-auto ms-2">
                                <FilePickerButton id="load-request" className="btn-outline-secondary"
                                    loading={fileImporting}
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    onChange={handleLoadOnChange}
                                    text="Load"
                                    accepts=".mrpreq" />
                            </div>
                        </div>
                    </div>
                    <div className="col-auto align-self-center">
                        <ErrorAlert visible={pricer.error} />
                    </div>
                    <div className="col-auto align-self-center">
                        <div className="row g-0 justify-cotent-end">
                            <div className="col-auto">
                                <Button className="btn-outline-secondary" onClick={handleReset} disabled={pricer.loading || fileImporting || fileExporting} text="Reset" />
                            </div>
                            <div className="col-auto ms-2">
                                <Button className="btn-primary" disabled={pricer.loading || fileImporting || fileExporting} loading={pricer.loading} text="Submit" onClick={handleSubmit} />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
